import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Loader2, Gem, Lock, Truck, ShieldCheck, Zap, Check, Gauge, Layers, Weight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Reveal } from "./common";
import { useContent } from "../../context/ContentContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const empty = { name: "", phone: "", email: "", line: "", city: "", pincode: "", state: "" };

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const OrderForm = () => {
  const navigate = useNavigate();
  const { product, variants, paymentConfig } = useContent();
  const online = !!paymentConfig?.online_enabled;

  // Only active variants are selectable
  const activeVariants = (variants || []).filter((v) => v.active !== false);
  const firstAvailable = activeVariants.find((v) => v.stock == null || v.stock > 0) || activeVariants[0];

  const [qty, setQty] = useState(1);
  const [variantId, setVariantId] = useState(firstAvailable?.id || activeVariants[0]?.id);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [areas, setAreas] = useState([]);
  const [locLoading, setLocLoading] = useState(false);

  const onPincode = async (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setForm((f) => ({ ...f, pincode: val }));
    if (val.length !== 6) { setAreas([]); return; }
    setLocLoading(true);
    try {
      const r = await fetch(`https://api.postalpincode.in/pincode/${val}`);
      const d = await r.json();
      const po = d?.[0]?.PostOffice || [];
      if (po.length) {
        setAreas(po.map((p) => p.Name));
        setForm((f) => ({ ...f, pincode: val, area: po[0].Name, city: po[0].District || f.city, state: po[0].State || f.state }));
      } else {
        setAreas([]);
        toast.error("Pincode not found");
      }
    } catch (err) {
      setAreas([]);
    } finally {
      setLocLoading(false);
    }
  };

  const selected = activeVariants.find((v) => v.id === variantId) || activeVariants[0] || { price: product?.price || 0, label: product?.name };
  const taxPercent = product?.tax_percent || 3;
  const maxStock = selected?.stock != null ? selected.stock : (product?.stock || 10);
  const subtotal = (selected?.price || 0) * qty;
  const gst = Math.round(subtotal * taxPercent / 100);
  const total = Math.round(subtotal + gst);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const cur = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

  const validate = () => {
    if (!selected) return "Please select a variant";
    if (maxStock <= 0) return "Selected variant is out of stock";
    if (!form.name.trim()) return "Please enter your name";
    if (!/^\d{10}$/.test(form.phone.replace(/\D/g, "").slice(-10))) return "Enter a valid 10-digit phone number";
    if (!form.line.trim()) return "Please enter your address";
    if (!form.city.trim()) return "Please enter your city";
    if (!/^\d{6}$/.test(form.pincode.trim())) return "Enter a valid 6-digit pincode";
    return null;
  };

  const createOrder = async (payMethod) => {
    const body = {
      customer: { name: form.name, phone: form.phone, email: form.email },
      address: { line: form.line, city: form.city, state: form.state, pincode: form.pincode },
      items: [{ product_slug: product.slug, variant_id: variantId, quantity: qty }],
      payment_method: payMethod,
    };
    const res = await axios.post(`${API}/orders`, body);
    return res.data;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return; // prevent double submit
    const err = validate();
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
      if (online) {
        const order = await createOrder("online");
        const ok = await loadRazorpay();
        if (!ok) { toast.error("Could not load secure payment. Please try again."); setLoading(false); return; }
        const pay = await axios.post(`${API}/payments/create`, { order_id: order.id });
        const options = {
          key: pay.data.key_id,
          amount: pay.data.amount,
          currency: pay.data.currency,
          name: "Adrusta Ratnam",
          description: `${product.name} — ${selected.label}`,
          order_id: pay.data.razorpay_order_id,
          prefill: { name: form.name, email: form.email, contact: form.phone },
          theme: { color: "#5A0B1D" },
          handler: async (resp) => {
            try {
              const verified = await axios.post(`${API}/payments/verify`, {
                order_id: order.id,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              toast.success("Payment successful!");
              navigate("/thank-you", { state: { order: verified.data } });
            } catch (ver) {
              navigate("/thank-you", { state: { order, failed: true } });
            }
          },
          modal: { ondismiss: () => { toast("Payment cancelled"); setLoading(false); } },
        };
        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", () => { toast.error("Payment failed. Please retry."); setLoading(false); });
        rzp.open();
        return;
      }
      // Fallback while online payment is being activated: place as COD-confirmed order
      const order = await createOrder("cod");
      toast.success("Order placed successfully!");
      navigate("/thank-you", { state: { order } });
    } catch (e2) {
      const msg = e2?.response?.data?.detail || "Something went wrong. Please try again or call us.";
      toast.error(msg);
    } finally {
      if (!online) setLoading(false);
    }
  };

  return (
    <section id="order-form" className="py-16" style={{ background: "linear-gradient(180deg,#F3E7CE,#FBF5E9)" }}>
      <div className="max-w-5xl mx-auto px-4">
        <Reveal>
          <div className="text-center mb-8">
            <span className="font-marcellus tracking-[0.25em] text-xs uppercase text-[#B8860B]">Start Your Journey Today</span>
            <h2 className="font-display text-2xl sm:text-4xl font-bold text-[#5A0B1D] mt-2">Reserve Your Adrusta Ratnam</h2>
            <div className="section-divider w-28 mx-auto mt-4" />
            <p className="text-[#8a6d2f] text-sm mt-3">Choose your variant below</p>
          </div>
        </Reveal>

        {/* dynamic variant selection */}
        <Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {activeVariants.map((v) => {
              const active = v.id === variantId;
              const out = v.stock != null && v.stock <= 0;
              return (
                <button type="button" key={v.id} disabled={out}
                  onClick={() => { if (!out) { setVariantId(v.id); setQty(1); } }}
                  className={`text-left rounded-2xl border-2 p-4 transition-all relative overflow-hidden ${out ? "opacity-60 cursor-not-allowed border-[#e0d0aa]" : active ? "border-[#C9992B] bg-white shadow-lg" : "border-[#e0d0aa] bg-white/70 hover:border-[#C9992B]/60"}`}>
                  {v.badge && (
                    <span className={`absolute -top-3 right-4 text-[10px] font-bold uppercase tracking-wide px-3 py-1 rounded-full ${v.highlight ? "bg-[#5A0B1D] text-[#F4E3B8]" : "bg-[#C9992B] text-[#3a1002]"}`}>
                      {v.highlight && <Zap className="w-3 h-3 inline -mt-0.5 mr-1" />}{v.badge}
                    </span>
                  )}
                  <div className="flex gap-3">
                    <img src={v.image || (product?.images || [])[0]} alt={v.label}
                      className="w-16 h-16 rounded-lg object-cover border border-[#e0d0aa] bg-[#FBF5E9] shrink-0" />
                    <div className="min-w-0">
                      <div className="font-display font-bold text-[#5A0B1D] leading-tight">{v.label}</div>
                      <div className="text-xs text-[#8a6d2f] mt-0.5 truncate">{v.gemstone || v.spec}</div>
                      <div className="font-display text-xl font-bold text-[#B8860B] mt-1">{cur(v.price)}</div>
                    </div>
                    <div className={`ml-auto shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${active ? "border-[#C9992B] bg-[#C9992B]" : "border-[#c9b899]"}`}>
                      {active && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 text-[11px] text-[#6b5647]">
                    {v.carat && <span className="inline-flex items-center gap-1"><Gauge className="w-3.5 h-3.5 text-[#C9992B]" />{v.carat}</span>}
                    {v.metal && <span className="inline-flex items-center gap-1"><Layers className="w-3.5 h-3.5 text-[#C9992B]" />{v.metal}</span>}
                    {v.weight && <span className="inline-flex items-center gap-1"><Weight className="w-3.5 h-3.5 text-[#C9992B]" />{v.weight}</span>}
                  </div>
                  <div className="mt-2 text-[11px] font-medium">
                    {out ? <span className="text-red-500">Out of stock</span>
                      : v.stock != null && v.stock <= 3 ? <span className="text-amber-600">Only {v.stock} left</span>
                      : <span className="text-emerald-600">In stock</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </Reveal>

        <div className="grid md:grid-cols-5 gap-6">
          {/* dynamic summary */}
          <Reveal className="md:col-span-2">
            <div className="bg-[#5A0B1D] rounded-2xl p-6 text-[#F4E3B8] h-full">
              <div className="flex items-center gap-3">
                <img src={selected?.image || (product?.images && product.images[0]) || ""} alt="" className="w-16 h-16 rounded-lg object-cover border-2 border-[#C9992B]/50" />
                <div>
                  <div className="font-display font-bold">{product?.name}</div>
                  <div className="text-xs text-[#c9b59a]">{selected?.label}</div>
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between">
                <span className="text-sm text-[#e0c9ac]">Quantity</span>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} className="w-8 h-8 rounded-full bg-[#6b1024] hover:bg-[#7a1226] text-lg">−</button>
                  <span className="w-8 text-center font-bold">{qty}</span>
                  <button type="button" onClick={() => setQty(Math.min(maxStock, qty + 1))} className="w-8 h-8 rounded-full bg-[#6b1024] hover:bg-[#7a1226] text-lg">+</button>
                </div>
              </div>
              <div className="section-divider my-4 opacity-40" />
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-[#e0c9ac]">Subtotal</span><span>{cur(subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-[#e0c9ac]">GST ({taxPercent}%)</span><span>{cur(gst)}</span></div>
                <div className="flex justify-between"><span className="text-[#e0c9ac]">Delivery</span><span>Free</span></div>
              </div>
              <div className="section-divider my-4 opacity-40" />
              <div className="flex justify-between font-bold text-lg"><span>Total</span><span className="text-[#E9C767]">{cur(total)}</span></div>
              <div className="mt-5 space-y-2 text-xs text-[#e0c9ac]">
                <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#E9C767]" /> Free energisation &amp; certificate</div>
                <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#E9C767]" /> Insured delivery to your door</div>
                <div className="flex items-center gap-2"><Lock className="w-4 h-4 text-[#E9C767]" /> Secure payment &amp; server-verified amount</div>
              </div>
            </div>
          </Reveal>

          {/* customer details + payment */}
          <Reveal delay={120} className="md:col-span-3">
            <form onSubmit={submit} className="bg-white rounded-2xl border border-[#e7d9b8] p-6 shadow-sm space-y-4">
              <Field label="Full Name" value={form.name} onChange={set("name")} placeholder="Your full name" />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Phone Number" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" type="tel" />
                <Field label="Email (optional)" value={form.email} onChange={set("email")} placeholder="you@email.com" type="email" required={false} />
              </div>
              <Field label="Full Address" value={form.line} onChange={set("line")} placeholder="House / street / area" />
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#5A0B1D] text-sm font-medium mb-1.5 block">Pincode <span className="text-[#b8860b]">*</span></Label>
                  <Input value={form.pincode} onChange={onPincode} placeholder="6-digit" inputMode="numeric"
                    className="border-[#d8c7a0] focus-visible:ring-[#C9992B] bg-[#FBF5E9]/40" />
                  {locLoading && <span className="text-xs text-[#8a6d2f] mt-1 inline-block">Detecting location…</span>}
                </div>
                {areas.length > 0 ? (
                  <div>
                    <Label className="text-[#5A0B1D] text-sm font-medium mb-1.5 block">Area / Locality</Label>
                    <select value={form.area || ""} onChange={(e) => setForm({ ...form, area: e.target.value })}
                      className="w-full h-10 rounded-md border border-[#d8c7a0] bg-[#FBF5E9]/40 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9992B]">
                      <option value="">Select area</option>
                      {areas.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                ) : (
                  <Field label="City" value={form.city} onChange={set("city")} placeholder="City" />
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {areas.length > 0 && <Field label="City" value={form.city} onChange={set("city")} placeholder="City" />}
                <Field label="State" value={form.state} onChange={set("state")} placeholder="State (auto)" required={false} />
              </div>

              <div className="rounded-xl border-2 border-[#C9992B] bg-[#FBF5E9] p-4 flex items-center gap-3">
                <Lock className="w-5 h-5 text-[#B8860B]" />
                <div>
                  <div className="font-semibold text-[#5A0B1D] text-sm">Secure Online Payment</div>
                  <div className="text-xs text-[#8a6d2f]">{online ? "Pay safely via UPI, cards, netbanking & wallets" : "Activating shortly — your order will be confirmed and our team will collect payment"}</div>
                </div>
              </div>

              <button type="submit" disabled={loading || maxStock <= 0}
                className="cta-pulse w-full rounded-xl py-4 font-display font-extrabold uppercase text-[#3a1002] text-base sm:text-lg flex items-center justify-center gap-2 disabled:opacity-70"
                style={{ background: "linear-gradient(180deg,#F2D06B,#C9992B)", border: "2px solid #8a6410" }}>
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Gem className="w-5 h-5" />}
                {loading ? "Processing secure payment…" : maxStock <= 0 ? "Out Of Stock" : `Proceed To Secure Payment · ${cur(total)}`}
              </button>
              <p className="text-center text-xs text-[#8a6d2f]"><Lock className="w-3 h-3 inline -mt-0.5 mr-1" /> Final amount is validated on our server. Our team will call to confirm delivery.</p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, required = true, ...props }) => (
  <div>
    <Label className="text-[#5A0B1D] text-sm font-medium mb-1.5 block">{label} {required && <span className="text-[#b8860b]">*</span>}</Label>
    <Input {...props} className="border-[#d8c7a0] focus-visible:ring-[#C9992B] bg-[#FBF5E9]/40" />
  </div>
);

export default OrderForm;
